import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  inventoryAdjustmentsApi,
  inventoryAdjustmentsQueryKeys,
} from "../../shared/api/inventoryAdjustments";
import { apiError } from "../../shared/api/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

/** T72 — Điều chỉnh tồn kho thủ công (nhập/xuất trên lô sẵn có). */
export function AdjustmentsPage() {
  const qc = useQueryClient();
  const stockQ = useQuery({
    queryKey: inventoryAdjustmentsQueryKeys.stock(),
    queryFn: () => inventoryAdjustmentsApi.stock(),
  });
  const historyQ = useQuery({
    queryKey: inventoryAdjustmentsQueryKeys.history(),
    queryFn: () => inventoryAdjustmentsApi.history(),
  });
  const medicinesQ = useQuery({
    queryKey: inventoryAdjustmentsQueryKeys.medicines(),
    queryFn: () => inventoryAdjustmentsApi.medicines(),
  });

  // Nhập kho lô mới
  const [rcvMedicineId, setRcvMedicineId] = useState("");
  const [rcvLot, setRcvLot] = useState("");
  const [rcvExpiry, setRcvExpiry] = useState("");
  const [rcvQty, setRcvQty] = useState(1);
  const [rcvCost, setRcvCost] = useState("");
  const [rcvMsg, setRcvMsg] = useState<string | null>(null);
  const [rcvError, setRcvError] = useState<string | null>(null);

  const receive = useMutation({
    mutationFn: () =>
      inventoryAdjustmentsApi.receive({
        medicineId: rcvMedicineId,
        lotNumber: rcvLot.trim(),
        expiryDate: rcvExpiry,
        quantity: Math.abs(rcvQty),
        costPerUnitVnd: rcvCost ? Number.parseInt(rcvCost, 10) : null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: inventoryAdjustmentsQueryKeys.stock() });
      qc.invalidateQueries({ queryKey: inventoryAdjustmentsQueryKeys.history() });
      setRcvMsg("Đã nhập kho lô mới.");
      setRcvError(null);
      setRcvLot("");
      setRcvExpiry("");
      setRcvQty(1);
      setRcvCost("");
    },
    onError: (e: unknown) => {
      setRcvError(apiError(e).detail);
      setRcvMsg(null);
    },
  });

  const canReceive = rcvMedicineId && rcvLot.trim() && rcvExpiry && rcvQty > 0;

  const [inventoryId, setInventoryId] = useState("");
  const [direction, setDirection] = useState<"in" | "out">("out");
  const [qty, setQty] = useState(1);
  const [reason, setReason] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const selected = useMemo(
    () => stockQ.data?.find((s) => s.inventoryId === inventoryId),
    [stockQ.data, inventoryId]
  );

  const save = useMutation({
    mutationFn: () =>
      inventoryAdjustmentsApi.adjust({
        inventoryId,
        quantity: direction === "out" ? -Math.abs(qty) : Math.abs(qty),
        reason: reason.trim(),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: inventoryAdjustmentsQueryKeys.stock() });
      qc.invalidateQueries({ queryKey: inventoryAdjustmentsQueryKeys.history() });
      setMsg("Đã ghi nhận điều chỉnh.");
      setError(null);
      setQty(1);
      setReason("");
    },
    onError: (e: unknown) => {
      setError(apiError(e).detail);
      setMsg(null);
    },
  });

  const canSubmit = inventoryId && qty > 0 && reason.trim().length > 0;

  return (
    <div className="font-sans">
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-foreground">
          Điều chỉnh tồn kho
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Nhập / xuất kho thủ công trên lô sẵn có (mất, hủy, kiểm kê chênh lệch).
        </p>
      </div>

      <div className="grid max-w-[820px] gap-4">
        {/* Receive new batch */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Nhập kho lô mới</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="rcv-medicine">Thuốc</Label>
              <Select
                value={rcvMedicineId}
                onValueChange={(v) => setRcvMedicineId(v)}
              >
                <SelectTrigger id="rcv-medicine">
                  <SelectValue placeholder="— Chọn thuốc —" />
                </SelectTrigger>
                <SelectContent>
                  {(medicinesQ.data ?? []).map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="rcv-lot">Số lô</Label>
                <Input
                  id="rcv-lot"
                  type="text"
                  value={rcvLot}
                  onChange={(e) => setRcvLot(e.target.value)}
                  maxLength={64}
                  placeholder="VD: LOT-2026-01"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="rcv-expiry">Hạn sử dụng</Label>
                <Input
                  id="rcv-expiry"
                  type="date"
                  value={rcvExpiry}
                  onChange={(e) => setRcvExpiry(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="rcv-qty">Số lượng</Label>
                <Input
                  id="rcv-qty"
                  type="number"
                  min={1}
                  value={rcvQty}
                  onChange={(e) => setRcvQty(Number.parseInt(e.target.value || "0", 10))}
                  className="w-[100px]"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="rcv-cost">Giá vốn/đơn vị (VND)</Label>
                <Input
                  id="rcv-cost"
                  type="number"
                  min={0}
                  value={rcvCost}
                  onChange={(e) => setRcvCost(e.target.value)}
                  className="w-[140px]"
                  placeholder="tuỳ chọn"
                />
              </div>
            </div>
            {rcvError && <p className="text-sm text-destructive">{rcvError}</p>}
            {rcvMsg && <p className="text-sm text-emerald-600">{rcvMsg}</p>}
            <div className="flex items-center justify-end gap-3">
              {!canReceive && (
                <span className="text-xs text-muted-foreground">
                  Cần chọn thuốc, nhập số lô, hạn sử dụng và số lượng.
                </span>
              )}
              <Button
                type="button"
                title={canReceive ? undefined : "Điền đủ thuốc, số lô, hạn dùng, số lượng"}
                disabled={!canReceive || receive.isPending}
                onClick={() => {
                  setRcvMsg(null);
                  setRcvError(null);
                  receive.mutate();
                }}
              >
                {receive.isPending ? "Đang nhập…" : "Nhập kho"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Adjustment form */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Điều chỉnh lô sẵn có</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3">
            {!stockQ.isLoading && (stockQ.data ?? []).length === 0 && (
              <p className="rounded-md bg-muted px-3 py-2 text-[13px] text-muted-foreground">
                Chưa có lô nào trong kho. Hãy dùng “Nhập kho lô mới” ở trên để nhập hàng
                trước, sau đó mới điều chỉnh (xuất/hủy/kiểm kê) được.
              </p>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="adj-inventory">Lô thuốc</Label>
              <Select value={inventoryId} onValueChange={(v) => setInventoryId(v)}>
                <SelectTrigger id="adj-inventory">
                  <SelectValue placeholder="— Chọn lô —" />
                </SelectTrigger>
                <SelectContent>
                  {(stockQ.data ?? []).map((s) => (
                    <SelectItem key={s.inventoryId} value={s.inventoryId}>
                      {s.medicineName}
                      {s.lotNumber ? ` · Lô ${s.lotNumber}` : ""}
                      {s.expiryDate ? ` · HSD ${s.expiryDate}` : ""} · Tồn: {s.quantityOnHand}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <label className="flex items-center gap-1.5">
                <input
                  type="radio"
                  name="dir"
                  checked={direction === "out"}
                  onChange={() => setDirection("out")}
                />
                <span>Xuất (giảm)</span>
              </label>
              <label className="flex items-center gap-1.5">
                <input
                  type="radio"
                  name="dir"
                  checked={direction === "in"}
                  onChange={() => setDirection("in")}
                />
                <span>Nhập (tăng)</span>
              </label>
              <div className="flex items-center gap-2">
                <Label htmlFor="adj-qty">Số lượng</Label>
                <Input
                  id="adj-qty"
                  type="number"
                  min={1}
                  value={qty}
                  onChange={(e) => setQty(Number.parseInt(e.target.value || "0", 10))}
                  className="w-[100px]"
                />
              </div>
              {selected && direction === "out" && qty > selected.quantityOnHand && (
                <span className="text-xs text-destructive">
                  Vượt quá tồn kho ({selected.quantityOnHand}).
                </span>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="adj-reason">Lý do</Label>
              <Input
                id="adj-reason"
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="VD: Hỏng vỡ / Kiểm kê chênh lệch / Trả nhà cung cấp"
                maxLength={500}
              />
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}
            {msg && <p className="text-sm text-emerald-600">{msg}</p>}

            <div className="flex items-center justify-end gap-3">
              {(stockQ.data ?? []).length > 0 && !canSubmit && (
                <span className="text-xs text-muted-foreground">
                  Cần chọn lô, số lượng và nhập lý do.
                </span>
              )}
              <Button
                type="button"
                title={canSubmit ? undefined : "Chọn lô, số lượng và nhập lý do"}
                disabled={!canSubmit || save.isPending}
                onClick={() => {
                  setMsg(null);
                  setError(null);
                  save.mutate();
                }}
              >
                {save.isPending ? "Đang lưu…" : "Ghi nhận điều chỉnh"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Current stock */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Tồn kho hiện tại</CardTitle>
          </CardHeader>
          <CardContent>
            {stockQ.isLoading ? (
              <p className="text-sm text-muted-foreground">Đang tải…</p>
            ) : (stockQ.data ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">Chưa có tồn kho.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Thuốc</TableHead>
                    <TableHead>Lô</TableHead>
                    <TableHead>HSD</TableHead>
                    <TableHead className="text-right">Tồn</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stockQ.data!.map((s) => (
                    <TableRow key={s.inventoryId}>
                      <TableCell>{s.medicineName}</TableCell>
                      <TableCell>{s.lotNumber ?? "—"}</TableCell>
                      <TableCell>{s.expiryDate ?? "—"}</TableCell>
                      <TableCell className="text-right">{s.quantityOnHand}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* History */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Lịch sử điều chỉnh</CardTitle>
          </CardHeader>
          <CardContent>
            {historyQ.isLoading ? (
              <p className="text-sm text-muted-foreground">Đang tải…</p>
            ) : (historyQ.data ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">Chưa có điều chỉnh nào.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Thời gian</TableHead>
                    <TableHead>Thuốc</TableHead>
                    <TableHead>Lô</TableHead>
                    <TableHead className="text-right">SL</TableHead>
                    <TableHead>Lý do</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {historyQ.data!.map((h, i) => (
                    <TableRow key={i}>
                      <TableCell>{new Date(h.at).toLocaleString("vi-VN")}</TableCell>
                      <TableCell>{h.medicineName}</TableCell>
                      <TableCell>{h.lotNumber ?? "—"}</TableCell>
                      <TableCell
                        className={cn(
                          "text-right",
                          h.quantity < 0 ? "text-destructive" : "text-emerald-600"
                        )}
                      >
                        {h.quantity > 0 ? `+${h.quantity}` : h.quantity}
                      </TableCell>
                      <TableCell>{h.reason ?? "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
